import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Toaster } from "sonner";
import { Loader2, LogOut, Trash2, Check, X, Clock, Mail, Car, Home as HomeIcon, Sparkles, Upload, Image as ImageIcon, KeyRound } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { api, API, BACKEND, BUSINESS } from "@/lib/api";

function Login({ onLogin }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!password) return toast.error("Enter your password");
    setLoading(true);
    try {
      const { data } = await api.post("/admin/login", { password });
      localStorage.setItem("padmin_token", data.token);
      toast.success("Welcome back.");
      onLogin();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground grain flex items-center justify-center px-6">
      <form onSubmit={submit} className="w-full max-w-md bg-card border gold-border clip-frame p-10" data-testid="admin-login-form">
        <div className="mb-8 text-center">
          <span className="font-serif text-3xl">Platinum</span>
          <div className="text-[10px] tracking-[0.35em] uppercase text-primary mt-1">Admin</div>
        </div>
        <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-3">Owner password</p>
        <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter password" autoFocus data-testid="admin-password-input" className="bg-background border-border h-12 mb-6" />
        <button type="submit" disabled={loading} data-testid="admin-login-btn" className="w-full py-3.5 bg-primary text-primary-foreground text-sm tracking-wide hover:bg-primary/90 transition-colors clip-frame flex items-center justify-center gap-2 disabled:opacity-60">
          {loading && <Loader2 className="w-4 h-4 animate-spin" />} Sign in
        </button>
        <a href="/" className="mt-6 block text-center text-xs tracking-wide text-muted-foreground hover:text-primary">&larr; Back to site</a>
      </form>
    </div>
  );
}

function Badge({ status }) {
  const map = {
    pending: "border-primary/40 text-primary",
    confirmed: "border-emerald-500/40 text-emerald-400",
    completed: "border-muted-foreground/40 text-muted-foreground",
    cancelled: "border-destructive/50 text-destructive",
    new: "border-primary/40 text-primary",
    responded: "border-emerald-500/40 text-emerald-400",
  };
  return <span className={`px-2 py-1 text-[10px] tracking-widest uppercase border ${map[status] || "border-border text-foreground"}`}>{status}</span>;
}

function ChangePasswordDialog() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.current_password || !form.new_password) return toast.error("Fill in both password fields");
    if (form.new_password.length < 8) return toast.error("New password must be at least 8 characters");
    if (form.new_password !== form.confirm) return toast.error("New password confirmation does not match");
    setSaving(true);
    try {
      await api.post("/admin/change-password", { current_password: form.current_password, new_password: form.new_password });
      toast.success("Password changed");
      setOpen(false);
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not change password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="flex items-center gap-2 text-xs tracking-wide text-muted-foreground hover:text-primary" data-testid="admin-change-password-btn">
          <KeyRound className="w-3.5 h-3.5" /> Change password
        </button>
      </DialogTrigger>
      <DialogContent className="bg-card border-border max-w-md" data-testid="admin-change-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Change password</DialogTitle>
          <DialogDescription className="text-muted-foreground">Choose a new owner password. Minimum 8 characters.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 pt-2">
          <Input type="password" placeholder="Current password" value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} data-testid="cp-current" className="bg-background border-border h-11" />
          <Input type="password" placeholder="New password" value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} data-testid="cp-new" className="bg-background border-border h-11" />
          <Input type="password" placeholder="Confirm new password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} data-testid="cp-confirm" className="bg-background border-border h-11" />
          <DialogFooter className="pt-2">
            <button type="button" onClick={() => setOpen(false)} className="px-6 py-2.5 text-sm text-muted-foreground hover:text-foreground transition-colors">Cancel</button>
            <button type="submit" disabled={saving} data-testid="cp-submit" className="px-6 py-2.5 bg-primary text-primary-foreground text-sm tracking-wide hover:bg-primary/90 transition-colors clip-frame flex items-center gap-2 disabled:opacity-60">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Dashboard({ onLogout }) {
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, c, q, g] = await Promise.all([
        api.get("/admin/bookings"),
        api.get("/admin/contacts"),
        api.get("/admin/quotes"),
        api.get("/admin/gallery"),
      ]);
      setBookings(b.data); setContacts(c.data); setQuotes(q.data); setGallery(g.data);
    } catch (e) {
      if (e?.response?.status === 401) onLogout();
      else toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => { load(); }, [load]);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/admin/gallery", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setGallery((prev) => [{ id: data.id, url: data.url, created_at: new Date().toISOString() }, ...prev]);
      }
      toast.success(`${files.length} photo(s) uploaded`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const deleteImage = async (id) => {
    if (!window.confirm("Remove this photo from the gallery?")) return;
    try {
      await api.delete(`/admin/gallery/${id}`);
      setGallery((prev) => prev.filter((g) => g.id !== id));
      toast.success("Removed");
    } catch { toast.error("Delete failed"); }
  };

  const updateBooking = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}`, { status });
      toast.success(`Marked ${status}`);
      setBookings((prev) => prev.map((b) => b.id === id ? { ...b, status } : b));
    } catch { toast.error("Update failed"); }
  };

  const deleteBooking = async (id) => {
    if (!window.confirm("Delete this booking?")) return;
    try {
      await api.delete(`/admin/bookings/${id}`);
      setBookings((prev) => prev.filter((b) => b.id !== id));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  const updateQuote = async (id, status) => {
    try {
      await api.patch(`/admin/quotes/${id}`, { status });
      setQuotes((prev) => prev.map((q) => q.id === id ? { ...q, status } : q));
    } catch { toast.error("Update failed"); }
  };

  return (
    <div className="min-h-screen bg-background text-foreground grain">
      <header className="border-b border-border bg-black/60 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div>
            <span className="font-serif text-xl">Platinum</span>
            <span className="text-[10px] tracking-[0.3em] uppercase text-primary ml-2">Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" className="text-xs tracking-wide text-muted-foreground hover:text-foreground" data-testid="admin-view-site">View site</a>
            <ChangePasswordDialog />
            <button onClick={onLogout} data-testid="admin-logout-btn" className="flex items-center gap-2 text-xs tracking-wide text-muted-foreground hover:text-primary">
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12">
        <div className="mb-10">
          <h1 className="font-serif text-5xl tracking-tighter text-foreground">Command center</h1>
          <p className="text-muted-foreground font-light mt-2">Manage bookings, quote requests and customer messages.</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10" data-testid="admin-stats">
          {[
            { label: "Bookings", value: bookings.length, icon: Clock, testid: "stat-bookings" },
            { label: "Pending", value: bookings.filter(b => b.status === "pending").length, icon: Clock, testid: "stat-pending" },
            { label: "Quote requests", value: quotes.length, icon: Sparkles, testid: "stat-quotes" },
            { label: "Messages", value: contacts.length, icon: Mail, testid: "stat-messages" },
          ].map((s) => (
            <div key={s.label} className="p-5 bg-card border border-border clip-frame" data-testid={s.testid}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs tracking-[0.2em] uppercase text-muted-foreground">{s.label}</span>
                <s.icon className="w-4 h-4 text-primary" />
              </div>
              <div className="font-serif text-4xl text-foreground">{s.value}</div>
            </div>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : (
          <Tabs defaultValue="bookings">
            <TabsList className="bg-secondary border border-border mb-8 h-auto p-1">
              <TabsTrigger value="bookings" data-testid="admin-tab-bookings" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-sm px-6 py-2.5">Bookings ({bookings.length})</TabsTrigger>
              <TabsTrigger value="quotes" data-testid="admin-tab-quotes" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-sm px-6 py-2.5">Quotes ({quotes.length})</TabsTrigger>
              <TabsTrigger value="contacts" data-testid="admin-tab-contacts" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-sm px-6 py-2.5">Messages ({contacts.length})</TabsTrigger>
              <TabsTrigger value="gallery" data-testid="admin-tab-gallery" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-sm px-6 py-2.5">Gallery ({gallery.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="bookings">
              <div className="space-y-4" data-testid="admin-bookings-list">
                {bookings.length === 0 && <p className="text-muted-foreground font-light">No bookings yet.</p>}
                {bookings.map((b) => (
                  <div key={b.id} className="p-6 bg-card border border-border clip-frame" data-testid={`admin-booking-${b.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          {b.category === "auto" ? <Car className="w-4 h-4 text-primary" /> : <HomeIcon className="w-4 h-4 text-primary" />}
                          <h3 className="font-serif text-2xl text-foreground">{b.service_name}</h3>
                        </div>
                        <p className="text-sm text-muted-foreground">{b.date} · {b.time_slot}</p>
                      </div>
                      <Badge status={b.status} />
                    </div>
                    <div className="grid sm:grid-cols-3 gap-4 text-sm text-foreground/80 mb-4">
                      <div><span className="text-muted-foreground">Name</span><br />{b.name}</div>
                      <div><span className="text-muted-foreground">Email</span><br /><a href={`mailto:${b.email}`} className="hover:text-primary break-all">{b.email}</a></div>
                      <div><span className="text-muted-foreground">Phone</span><br /><a href={`tel:${b.phone}`} className="hover:text-primary">{b.phone}</a></div>
                    </div>
                    {b.notes && <p className="text-sm text-muted-foreground border-l-2 border-primary/40 pl-3 mb-4 italic">{b.notes}</p>}
                    <div className="flex flex-wrap gap-2">
                      <button onClick={() => updateBooking(b.id, "confirmed")} data-testid={`booking-confirm-${b.id}`} className="flex items-center gap-1 px-4 py-2 text-xs tracking-wide border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 transition-colors"><Check className="w-3.5 h-3.5" /> Confirm</button>
                      <button onClick={() => updateBooking(b.id, "completed")} data-testid={`booking-complete-${b.id}`} className="flex items-center gap-1 px-4 py-2 text-xs tracking-wide border border-border text-muted-foreground hover:text-foreground transition-colors"><Check className="w-3.5 h-3.5" /> Complete</button>
                      <button onClick={() => updateBooking(b.id, "cancelled")} data-testid={`booking-cancel-${b.id}`} className="flex items-center gap-1 px-4 py-2 text-xs tracking-wide border border-destructive/40 text-destructive hover:bg-destructive/10 transition-colors"><X className="w-3.5 h-3.5" /> Cancel</button>
                      <button onClick={() => deleteBooking(b.id)} data-testid={`booking-delete-${b.id}`} className="flex items-center gap-1 px-4 py-2 text-xs tracking-wide border border-border text-muted-foreground hover:text-destructive transition-colors ml-auto"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="quotes">
              <div className="space-y-4" data-testid="admin-quotes-list">
                {quotes.length === 0 && <p className="text-muted-foreground font-light">No quote requests yet.</p>}
                {quotes.map((q) => (
                  <div key={q.id} className="p-6 bg-card border border-border clip-frame" data-testid={`admin-quote-${q.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                      <div>
                        <span className="text-xs tracking-[0.25em] uppercase text-primary">{q.item_type}</span>
                        <h3 className="font-serif text-2xl text-foreground mt-1">{q.name}</h3>
                      </div>
                      <Badge status={q.status} />
                    </div>
                    <p className="text-foreground/80 mb-4">{q.item_description}</p>
                    <div className="grid sm:grid-cols-3 gap-4 text-sm text-foreground/80 mb-4">
                      <div><span className="text-muted-foreground">Email</span><br /><a href={`mailto:${q.email}`} className="hover:text-primary break-all">{q.email}</a></div>
                      <div><span className="text-muted-foreground">Phone</span><br /><a href={`tel:${q.phone}`} className="hover:text-primary">{q.phone}</a></div>
                      <div><span className="text-muted-foreground">Timeline</span><br />{q.preferred_timeline || "—"}</div>
                    </div>
                    {q.notes && <p className="text-sm text-muted-foreground border-l-2 border-primary/40 pl-3 mb-4 italic">{q.notes}</p>}
                    <div className="flex gap-2">
                      <button onClick={() => updateQuote(q.id, "responded")} data-testid={`quote-responded-${q.id}`} className="flex items-center gap-1 px-4 py-2 text-xs tracking-wide border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 transition-colors"><Check className="w-3.5 h-3.5" /> Mark responded</button>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="contacts">
              <div className="space-y-4" data-testid="admin-contacts-list">
                {contacts.length === 0 && <p className="text-muted-foreground font-light">No messages yet.</p>}
                {contacts.map((c) => (
                  <div key={c.id} className="p-6 bg-card border border-border clip-frame" data-testid={`admin-contact-${c.id}`}>
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <h3 className="font-serif text-2xl text-foreground">{c.name}</h3>
                      <span className="text-xs text-muted-foreground">{new Date(c.created_at).toLocaleString()}</span>
                    </div>
                    <div className="text-sm text-muted-foreground mb-3">
                      <a href={`mailto:${c.email}`} className="hover:text-primary">{c.email}</a>
                      {c.phone && <> · <a href={`tel:${c.phone}`} className="hover:text-primary">{c.phone}</a></>}
                    </div>
                    <p className="text-foreground/80 leading-relaxed">{c.message}</p>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="gallery">
              <div data-testid="admin-gallery-panel">
                <div className="mb-6 p-6 border-2 border-dashed border-border clip-frame flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <p className="text-xs tracking-[0.25em] uppercase text-primary mb-1">Upload photos</p>
                    <p className="text-sm text-muted-foreground">JPG, PNG or WebP. Up to 6 MB each. Photos appear in the &ldquo;Work&rdquo; section of your site.</p>
                  </div>
                  <label className="cursor-pointer flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground text-sm tracking-wide hover:bg-primary/90 transition-colors clip-frame">
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    {uploading ? "Uploading…" : "Upload photos"}
                    <input type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={onUpload} className="hidden" data-testid="admin-gallery-upload-input" />
                  </label>
                </div>

                {gallery.length === 0 ? (
                  <div className="text-center py-16 border border-border clip-frame">
                    <ImageIcon className="w-8 h-8 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground font-light">No photos yet. Upload your first before/after shot.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {gallery.map((g) => (
                      <div key={g.id} className="relative group aspect-square overflow-hidden clip-frame border border-border" data-testid={`admin-gallery-item-${g.id}`}>
                        <img src={`${BACKEND}${g.url}`} alt="" className="w-full h-full object-cover" />
                        <button onClick={() => deleteImage(g.id)} data-testid={`admin-gallery-delete-${g.id}`} className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center bg-black/70 backdrop-blur-sm text-destructive opacity-0 group-hover:opacity-100 transition-opacity">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        )}
      </main>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(!!localStorage.getItem("padmin_token"));

  useEffect(() => {
    if (!authed) return;
    api.get("/admin/me").catch(() => {
      localStorage.removeItem("padmin_token");
      setAuthed(false);
    });
  }, [authed]);

  const logout = () => {
    localStorage.removeItem("padmin_token");
    setAuthed(false);
    toast.info("Signed out");
  };

  return (
    <>
      <Toaster theme="dark" position="top-right" richColors />
      {authed ? <Dashboard onLogout={logout} /> : <Login onLogin={() => setAuthed(true)} />}
    </>
  );
}
